const _ = require('lodash');

const EXCLUDED_FIELDS_FROM_COMPARING = [
  'version',
  'createdAt',
  'updatedAt',
  'Tenant',
  'devicedriver',
  'deviceSyncStatus',
  'productiveversion',
  'isProductiveVersion',
  'draftversion',
  'scope',
];

const DETAIL_DISPLAY_MESSAGE = 'Detail - display of changes not supported. Use toggle in header to compare manually';

function versionsHasUnsyncedChanges(template) {
  const DraftVersion = template.DraftVersion || template.draftversion || template;
  const ProductiveVersion = template.ProductiveVersion || template.productiveversion || template;

  if (!DraftVersion || !ProductiveVersion) {
    return true;
  }
  // Compare root level fields
  for (let key in DraftVersion) {
    if (Object.prototype.hasOwnProperty.call(DraftVersion, key)) {
      if (EXCLUDED_FIELDS_FROM_COMPARING.includes(key)) {
        continue;
      }
      const draftValue = DraftVersion[key];
      const productiveValue = ProductiveVersion[key];

      if (_.isObject(draftValue) && _.isObject(productiveValue)) {
        // Deep compare objects
        if (!_.isEqual(draftValue, productiveValue)) {
          return true;
        }
      } else if (!_.isObject(draftValue) && !_.isObject(productiveValue)) {
        // Compare primitive types
        if (draftValue !== productiveValue) {
          return true;
        }
      } else {
        // One is an object and the other is not
        return true;
      }
    }
  }

  // Check if there are any extra properties in ProductiveVersion that are not in DraftVersion
  for (let key in ProductiveVersion) {
    if (
      Object.prototype.hasOwnProperty.call(ProductiveVersion, key) &&
      !Object.prototype.hasOwnProperty.call(DraftVersion, key) &&
      !EXCLUDED_FIELDS_FROM_COMPARING.includes(key)
    ) {
      return true;
    }
  }

  return false;
}

function getUnsyncedChanges(template) {
  const differences = [];
  const DraftVersion = template.draftversion || template;
  const ProductiveVersion = template.productiveversion || template;

  if (!DraftVersion || !ProductiveVersion) {
    return [{ modification: 'Missing version', valueBeforeSync: ProductiveVersion, valueAfterSync: DraftVersion }];
  }

  const draftKeysToCompare = _.keys(DraftVersion).filter((key) => !EXCLUDED_FIELDS_FROM_COMPARING.includes(key));
  const productiveKeysToCompare = _.keys(ProductiveVersion).filter(
    (key) => !EXCLUDED_FIELDS_FROM_COMPARING.includes(key)
  );
  const keysToCompare = _.union(draftKeysToCompare, productiveKeysToCompare);

  const convertInReadableFormat = (value, key) => {
    if (!value || (_.isObject(value) && _.isEmpty(value))) {
      return '-';
    }

    if (key === 'customProperties') {
      const result = [];
      _.forOwn(value, (value, key) => {
        const defaultValue = value.defaultValue;
        const requiredValue = value.requiredValue ? 'value required' : 'value not required';
        result.push(`key: ${key}, default value: ${defaultValue}, ${requiredValue} `);
      });
      return result.join(' | ');
    }

    if (_.isArray(value)) {
      return value;
    } else if (_.isObject(value)) {
      return JSON.stringify(value);
    }

    return value;
  };

  const deepCompareObjects = (draftObj, productiveObj) => {
    const objDifferences = [];
    const draftObjKeys = _.keys(draftObj);
    const productiveObjKeys = _.keys(productiveObj);
    const allObjKeys = _.union(draftObjKeys, productiveObjKeys);

    allObjKeys.forEach((objKey) => {
      const draftValue = draftObj[objKey];
      const productiveValue = productiveObj[objKey];

      if (_.has(draftObj, objKey) && _.has(productiveObj, objKey)) {
        if (!_.isEqual(draftValue, productiveValue)) {
          objDifferences.push({
            modification: `Modified ${_.startCase(objKey).toLowerCase()}`,
            valueBeforeSync: objKey === 'health' ? '-' : convertInReadableFormat(productiveValue, objKey),
            valueAfterSync: objKey === 'health' ? DETAIL_DISPLAY_MESSAGE : convertInReadableFormat(draftValue, objKey),
          });
        }
      } else if (_.has(draftObj, objKey) && !_.has(productiveObj, objKey)) {
        objDifferences.push({
          modification: `Added ${_.startCase(objKey).toLowerCase()}`,
          valueBeforeSync: '-',
          valueAfterSync: convertInReadableFormat(draftValue, objKey),
        });
      } else if (!_.has(draftObj, objKey) && _.has(productiveObj, objKey)) {
        objDifferences.push({
          modification: `Deleted ${_.startCase(objKey).toLowerCase()}`,
          valueBeforeSync: convertInReadableFormat(productiveValue, objKey),
          valueAfterSync: '-',
        });
      }
    });

    return objDifferences;
  };

  keysToCompare.forEach((key) => {
    const draftValue = DraftVersion[key];
    const productiveValue = ProductiveVersion[key];

    if (_.has(DraftVersion, key) && _.has(ProductiveVersion, key)) {
      if (_.isPlainObject(draftValue) && _.isPlainObject(productiveValue)) {
        if (!_.isEqual(draftValue, productiveValue)) {
          differences.push(...deepCompareObjects(draftValue, productiveValue));
        }
      } else if (_.isArray(draftValue) && _.isArray(productiveValue)) {
        const identifier = key === 'stateDefinition' ? 'key' : 'id';

        const commonKeys = _.intersection(_.map(draftValue, identifier), _.map(productiveValue, identifier));

        // Filter draftValue and productiveValue to exclude common keys
        const filteredDraftValue = draftValue.filter((e) => !commonKeys.includes(e[identifier]));
        const filteredProductiveValue = productiveValue.filter((e) => !commonKeys.includes(e[identifier]));

        // Calculate added elements
        const addedElements = _.differenceWith(
          filteredDraftValue,
          filteredProductiveValue,
          (a, b) => a[identifier] === b[identifier]
        );

        // Calculate removed elements
        const removedElements = _.differenceWith(
          filteredProductiveValue,
          filteredDraftValue,
          (a, b) => a[identifier] === b[identifier]
        );

        const commonElements = _.intersectionWith(
          draftValue,
          productiveValue,
          (a, b) => a[identifier] === b[identifier] && !_.isEqual(a, b)
        );

        if (commonElements.length > 0) {
          commonElements.forEach((e) => {
            differences.push({
              modification: `Updated ${key === 'stateDefinition' ? 'data state' : 'rule'} ${e[identifier]}`,
              valueBeforeSync: '-',
              valueAfterSync: DETAIL_DISPLAY_MESSAGE,
            });
          });
        }
        if (addedElements.length > 0) {
          addedElements.forEach((e) => {
            differences.push({
              modification: `Added ${key === 'stateDefinition' ? 'data state' : 'rule'} ${e[identifier]}`,
              valueBeforeSync: '-',
              valueAfterSync: DETAIL_DISPLAY_MESSAGE,
            });
          });
        }
        if (removedElements.length > 0) {
          removedElements.forEach((e) => {
            differences.push({
              modification: `Deleted ${key === 'stateDefinition' ? 'data state' : 'rule'} ${e[identifier]}`,
              valueBeforeSync: '-',
              valueAfterSync: DETAIL_DISPLAY_MESSAGE,
            });
          });
        }
      } else if (!_.isEqual(draftValue, productiveValue)) {
        differences.push({
          modification: `Modified ${key}`,
          valueBeforeSync: convertInReadableFormat(productiveValue, key),
          valueAfterSync: convertInReadableFormat(draftValue, key),
        });
      }
    } else if (_.has(DraftVersion, key) && !_.has(ProductiveVersion, key)) {
      differences.push({
        modification: `Deleted ${key}`,
        valueBeforeSync: '-',
        valueAfterSync: JSON.stringify(draftValue),
      });
    } else if (!_.has(DraftVersion, key) && _.has(ProductiveVersion, key)) {
      differences.push({
        modification: `Added ${key}`,
        valueBeforeSync: JSON.stringify(productiveValue),
        valueAfterSync: '-',
      });
    }
  });

  return differences;
}

export { versionsHasUnsyncedChanges, getUnsyncedChanges };
